import {
	autoinject,
	bindable,
	bindingMode,
	computedFrom,
	containerless,
	customElement,
	PLATFORM,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { QasSearchResult } from '~/api/qas-api';
import { QasApi } from '~/api/qas-api';
import { Country } from '~/components/field/common/country';
import { Postcode } from '~/components/field/common/postcode';
import { State } from '~/components/field/common/state';
import { Suburb } from '~/components/field/common/suburb';
import { Form } from '~/components/form/form';
import {
	baseFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { FieldModel, FormModel, Modifiable } from '~/models/form-model';
import { SettingsState } from '~/settings-state/settings-state';
import { FetchUtils } from '~/utils/fetch-utils';
import { ObjectUtils } from '~/utils/utils';

registerComponent('a-address', {
	name: 'Address',
	requirePath: PLATFORM.moduleName('components/field/group/address'),
	props: [
		...baseFieldProperties,
		{
			label: 'Address Type',
			name: 'addressType',
			type: 'Select',
			items: [
				{
					displayName: '(Invalid)',
					value: '',
				},
				{
					displayName: 'Residential',
					value: AddressType.Residential,
				},
				{
					displayName: 'Postal',
					value: AddressType.Postal,
				},
			],
		},
		{
			label: 'Line 1 Field Model',
			name: 'line1',
			type: 'FieldModel',
		},
		{
			label: 'Line 2 Field Model',
			name: 'line2',
			type: 'FieldModel',
		},
		{
			label: 'Line 3 Field Model',
			name: 'line3',
			type: 'FieldModel',
		},
		{
			label: 'Suburb Field Model',
			name: 'suburb',
			type: 'FieldModel',
		},
		{
			label: 'State Field Model',
			name: 'state',
			type: 'FieldModel',
		},
		{
			label: 'Postcode Field Model',
			name: 'postcode',
			type: 'FieldModel',
		},
		{
			label: 'Country Field Model',
			name: 'country',
			type: 'FieldModel',
		},
		{
			label: 'DPID Field Model',
			name: 'dpid',
			type: 'FieldModel',
		},
		{
			label: 'Copy Line 1 Field Model',
			name: 'copyLine1',
			type: 'FieldModel',
		},
		{
			label: 'Copy Line 2 Field Model',
			name: 'copyLine2',
			type: 'FieldModel',
		},
		{
			label: 'Copy Line 3 Field Model',
			name: 'copyLine3',
			type: 'FieldModel',
		},
		{
			label: 'Copy Suburb Field Model',
			name: 'copySuburb',
			type: 'FieldModel',
		},
		{
			label: 'Copy State Field Model',
			name: 'copyState',
			type: 'FieldModel',
		},
		{
			label: 'Copy Postcode Field Model',
			name: 'copyPostcode',
			type: 'FieldModel',
		},
		{
			label: 'Copy Country Field Model',
			name: 'copyCountry',
			type: 'FieldModel',
		},
		{
			label: 'Copy DPID Field Model',
			name: 'copyDpid',
			type: 'FieldModel',
		},
	],
	isSelectable: true,
});

interface CopyField {
	source: FieldModel; // the source field model from which the value is being copied
	oldValue?: string; // the original value of this field
}

export const enum AddressType {
	Residential = 'Res',
	Postal = 'Post',
}

@autoinject
@containerless
@customElement('a-address')
export class Address {
	private isQasSupported: boolean = false;

	private readonly qasLabel: string = 'Address Search';

	private qasValue: string = '';

	private qasValidationMessage = '';

	private readonly qasSearchType: 'QAS' = 'QAS';

	/**
	 * addressType is utilized by QAS services to determine
	 * whether USE_RES_ADDR_LINE_1_AND_2 or USE_POST_ADDR_LINE_1_AND_2 should be used
	 * when applying QAS data to the form fields.
	 */
	@bindable({
		defaultBindingMode: bindingMode.toView,
	})
	public readonly addressType: AddressType;

	@bindable({
		changeHandler: 'line1Changed',
	})
	public line1: FieldModel;
	private readonly line1Label: string = this.i18n.tr('address.line1');

	@bindable public line2: FieldModel;
	private readonly line2Label: string = this.i18n.tr('address.line2');

	@bindable public line3: FieldModel;
	private readonly line3Label: string = this.i18n.tr('address.line3');

	@bindable public suburb: FieldModel;
	private readonly suburbLabel: string = this.i18n.tr('address.suburb');

	@bindable public state: FieldModel;
	private readonly stateLabel: string = this.i18n.tr('address.state');

	@bindable public postcode: FieldModel;
	private readonly postcodeLabel: string = this.i18n.tr('address.postcode');

	@bindable public country: FieldModel;
	private readonly countryLabel: string = this.i18n.tr('address.country');

	@bindable public dpid: FieldModel;
	private readonly dpidLabel: string = this.i18n.tr('address.dpid');

	@bindable private copyLine1: FieldModel;
	@bindable private copyLine2: FieldModel;
	@bindable private copyLine3: FieldModel;
	@bindable private copySuburb: FieldModel;
	@bindable private copyState: FieldModel;
	@bindable private copyPostcode: FieldModel;
	@bindable private copyCountry: FieldModel;
	@bindable private copyDpid: FieldModel;

	@bindable private readonly isReadonly: boolean = true;

	@bindable public isCustomisationEditor: boolean = false;

	/**
	 * isManuallyEditing will be true if QAS is enabled and if the user
	 * clicks the edit button.
	 */
	private isManuallyEditing: boolean = false;

	@bindable({ changeHandler: 'copyCheckedChanged' })
	private copyChecked: boolean;
	private copyFieldMap: Map<FieldModel, CopyField> = new Map();

	// todo(Jake): 2018-10-31 - WEBUI-1122
	// Umit mentioned that we may need to give clients the
	// ability to modify labels of each field in the future.

	private readonly postcodeField?: Postcode;
	private readonly stateField?: State;
	private readonly suburbField?: Suburb;
	private readonly countryField?: Country;

	constructor(
		private readonly qasApi: QasApi,
		private readonly settingsState: SettingsState,
		private readonly i18n: I18N,
		private readonly parentForm: Form,
	) {
		this.isQasSupported = this.settingsState.state.qasEnabled;
	}

	private copyCheckedChanged(checked: boolean) {
		if (checked) {
			if (this.line1 && this.copyLine1) {
				this.copyFieldMap.set(this.line1, { source: this.copyLine1 });
			}
			if (this.line2 && this.copyLine2) {
				this.copyFieldMap.set(this.line2, { source: this.copyLine2 });
			}
			if (this.line3 && this.copyLine3) {
				this.copyFieldMap.set(this.line3, { source: this.copyLine3 });
			}
			if (this.suburb && this.copySuburb) {
				this.copyFieldMap.set(this.suburb, { source: this.copySuburb });
			}
			if (this.state && this.copyState) {
				this.copyFieldMap.set(this.state, { source: this.copyState });
			}
			if (this.postcode && this.copyPostcode) {
				this.copyFieldMap.set(this.postcode, {
					source: this.copyPostcode,
				});
			}
			if (this.country && this.copyCountry) {
				this.copyFieldMap.set(this.country, {
					source: this.copyCountry,
				});
			}
			if (this.dpid && this.copyDpid) {
				this.copyFieldMap.set(this.dpid, { source: this.copyDpid });
			}

			this.copyFieldMap.forEach((copyField, target) => {
				// save old values before copying new ones so changes can be undone
				if (!copyField.oldValue) {
					copyField.oldValue = target.value;
				}
				target.value = copyField.source.value;
				target.modifiable = Modifiable.FALSE;
				target.forceSave = true;
			});
		} else {
			this.copyFieldMap.forEach((copyField, target) => {
				// restore old values when unchecking box
				target.value = copyField.oldValue;
				target.modifiable = Modifiable.TRUE;
				target.forceSave = false;
				copyField.oldValue = undefined;
			});
		}
	}

	private line1Changed(
		fieldModel: FieldModel,
		oldFieldModel: FieldModel,
	): void {
		// NOTE: Jake: 2019-09-03 - WEBUI-2444
		// The line1 model is changed when a user closes editing a record in the slideout
		// or if they switch to another record, so we clear the QAS field when line1's model
		// is changed.
		// We are assuming that "line1" will always be bound to the field for this to work.
		this.qasValue = '';

		this.isQasSupported =
			this.settingsState.state.qasEnabled &&
			Address.isFieldModelInTableWithQasSupport(fieldModel);
	}

	private onClickEdit = (e): void => {
		if (this.isManuallyEditing) {
			this.isManuallyEditing = false;
		} else {
			this.isManuallyEditing = true;
			if (this.dpid) {
				// NOTE: Jake: 2019-09-05 - WEBUI-2449
				// If the user manually edits the address then
				// make the DPID invalid.
				this.dpid.value = '';
			}
		}
	};

	private onClickClear = (e): void => {
		this.qasValue = '';

		if (this.line1) {
			this.line1.value = '';
		}
		if (this.line2) {
			this.line2.value = '';
		}
		if (this.line3) {
			this.line3.value = '';
		}
		if (this.suburb) {
			this.suburb.value = '';
		}
		if (this.postcode) {
			this.postcode.value = '';
		}
		if (this.state) {
			this.state.value = '';
		}
		if (this.country) {
			this.country.value = '';
		}
		if (this.dpid) {
			this.dpid.value = '';
		}
	};

	private onSelectQAS = (searchResult: QasSearchResult): void => {
		this.qasValidationMessage = '';
		this.qasApi
			.getAddressFromSearchResult(searchResult.moniker)
			.then((record) => {
				if (ObjectUtils.isEmpty(record)) {
					throw new Error('Missing properties from address result.');
				}

				// NOTE: Jake: 2019-09-05 - WEBUI-2449
				// Handle what line the data needs to go into.
				let useAddrLine1And2;
				switch (this.addressType) {
					case AddressType.Residential:
						useAddrLine1And2 =
							this.settingsState.state.useResAddrLine1And2;
						break;

					case AddressType.Postal:
						useAddrLine1And2 =
							this.settingsState.state.usePostAddrLine1And2;
						break;

					default:
						useAddrLine1And2 = true;
				}

				if (useAddrLine1And2) {
					if (this.line1) {
						this.line1.value = record.line1;
					}
					if (this.line2) {
						this.line2.value = record.line2;
					}
					// NOTE: Jake: 2019-09-05 - WEBUI-2449
					// Not sure if we should clear this field so
					// I'm going to opt to leave it the same for now.
					// if (this.line3) {
					// 	this.line3.value = '';
					// }
				} else {
					// NOTE: Jake: 2019-09-05 - WEBUI-2449
					// Not sure if we should clear this field as
					// I suspect clients store "careOf" here.
					// if (this.line1) {
					// 	this.line1.value = '';
					// }
					if (this.line2) {
						this.line2.value = record.line1;
					}
					if (this.line3) {
						this.line3.value = record.line2;
					}
				}

				if (this.suburb) {
					this.suburb.value = record.locality;
				}
				if (this.postcode) {
					this.postcode.value = record.postcode;
				}
				if (this.state) {
					this.state.value = record.state;
				}
				if (this.dpid) {
					this.dpid.value = record.dpid;
				}
				if (this.country) {
					this.country.value = record.countryCode;
				}
			})
			.catch((error) => {
				this.qasValue = '';
				this.qasValidationMessage = FetchUtils.normalizeError(error);
			});
	};

	@computedFrom(
		'copyLine1',
		'copyLine2',
		'copyLine3',
		'copySuburb',
		'copyState',
		'copyPostcode',
		'copyCountry',
		'copyDpid',
	)
	private get copyEnabled(): boolean {
		let fieldCount = 0;
		if (this.copyLine1) {
			fieldCount++;
		}
		if (this.copyLine2) {
			fieldCount++;
		}
		if (this.copyLine3) {
			fieldCount++;
		}
		if (this.copySuburb) {
			fieldCount++;
		}
		if (this.copyState) {
			fieldCount++;
		}
		if (this.copyPostcode) {
			fieldCount++;
		}
		if (this.copyCountry) {
			fieldCount++;
		}
		if (this.copyDpid) {
			fieldCount++;
		}
		return fieldCount === 8;
	}

	@computedFrom('addressType')
	private get copyLabel(): string {
		switch (this.addressType) {
			case AddressType.Residential:
				return 'Same as Postal?';

			case AddressType.Postal:
				return 'Same as Residential?';
		}
		return '(Invalid)';
	}

	@computedFrom('isCustomisationEditor', 'configErrors')
	private get isDeactivated(): boolean {
		if (this.isCustomisationEditor) {
			return false;
		}
		return this.configErrors.length > 0;
	}

	/**
	 * configErrors is used to disable this field from working if its missing required
	 * configuration settings.
	 */
	@computedFrom(
		'line1',
		'addressType',
		'dpid',
		'copyEnabled',
		// NOTE(Jae): 2020-03-18
		// getFormModel() utilizes "parentForm.formModel"
		'parentForm.formModel',
	)
	private get configErrors(): string[] {
		const errors = [];
		if (!this.line1) {
			errors.push('Line 1 is not configured in customisation.');
		}
		if (this.copyEnabled && !this.addressType) {
			errors.push(
				'Address type must be configured in customisation when all the copy attributes are configured.',
			);
		}
		// NOTE(Jae): 2020-03-18
		// We check if the form model contains a *_DPID field. If it does, then throw
		// an error message if its not set.
		// We do this so that clients can't accidentally forget the field as the data is
		// rarely shown. (So people wont notice if its gotten wrong)
		const formModel = this.getFormModel();
		if (!this.dpid && Address.hasDPIDFieldInFormModel(formModel)) {
			errors.push('DPID field is not configured in customisation.');
		}
		return errors;
	}

	private getFormModel(): FormModel {
		return this.parentForm ? this.parentForm.formModel : undefined;
	}

	private static isFieldModelInTableWithQasSupport(
		fieldModel: FieldModel,
	): boolean {
		// NOTE: Lucas: 2019-09-11 - WEBUI-2481
		// The Investment Manager table is not QAS ready. This currently only effects the investment-manager-details form.
		// NOTE(Jae): 2020-03-18
		// IM is the prefix for the InvestmentManagerDetails table.
		return !(
			fieldModel &&
			fieldModel.name &&
			fieldModel.name.startsWith('IM')
		);
	}

	private static hasDPIDFieldInFormModel(formModel: FormModel): boolean {
		if (!formModel || !formModel.fields) {
			// If not wrapped in an <a-form>, we don't bother catching
			// this error as we mainly want to catch it for customisations.
			// ie. Make sure someone doesn't forget to configure it.
			return true;
		}
		let hasDPIDField: boolean = false;
		for (const fieldName in formModel.fields) {
			if (!formModel.fields.hasOwnProperty(fieldName)) {
				continue;
			}
			hasDPIDField =
				hasDPIDField ||
				fieldName.endsWith('_DPID') ||
				fieldName.indexOf('_DPID_') !== -1;
		}
		return hasDPIDField;
	}

	/**
	 * copyCheckboxName will return a globally unique name such as
	 * "D2z_Res_Addr_Line_1_QasSearchRes"
	 */
	@computedFrom('line1', 'addressType')
	private get qasName(): string {
		if (!this.line1) {
			return '';
		}
		const name = this.line1.name;
		switch (this.addressType) {
			case AddressType.Residential:
				return name + '_QasSearchRes';

			case AddressType.Postal:
				return name + '_QasSearchPost';
		}
	}

	/**
	 * copyCheckboxName will return a globally unique name such as
	 * "D2z_Res_Addr_Line_1_SameAsPost"
	 */
	@computedFrom('line1', 'addressType')
	private get copyCheckboxName(): string {
		const name = this.line1.name;
		switch (this.addressType) {
			case AddressType.Residential:
				return name + '_SameAsPost';

			case AddressType.Postal:
				return name + '_SameAsRes';
		}
		return '';
	}

	@computedFrom('isQasSupported', 'line1.modifiable')
	private get qasSearchFieldVisible(): boolean {
		return (
			this.isQasSupported &&
			this.line1 &&
			this.line1.modifiable !== Modifiable.FALSE
		);
	}

	@computedFrom('isQasSupported', 'isManuallyEditing')
	private get isQasFieldReadonly(): boolean {
		return this.isQasSupported && this.isManuallyEditing;
	}

	@computedFrom(
		'isReadonly',
		'isQasSupported',
		'isManuallyEditing',
		'line1.modifiable',
	)
	private get isAddressFieldReadonly(): boolean {
		const isReadonly =
			this.isReadonly &&
			this.line1 &&
			this.line1.modifiable === Modifiable.FALSE;
		const result =
			isReadonly ||
			(this.isQasSupported && this.isManuallyEditing === false);
		return result;
	}
}
